import { createContext, useContext, useEffect, useState } from "react";

// Create the cart context
const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const storedCart = localStorage.getItem("cart");
    return storedCart ? JSON.parse(storedCart) : [];
  });

  const [discount, setDiscount] = useState({});


  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

  const removeExpiredItems = (cart) => {
    const now = Date.now();
    return cart.filter(item => now - item.addedAt < SEVEN_DAYS_MS);
  };


  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
    const cleanedCart = removeExpiredItems(storedCart);
    setCart(cleanedCart);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
    setCartItemCount(cart.map(item => item.quantity).reduce((a, b) => a + b, 0));
  }, [cart, discount]);


  function setCartItemCount(count) {
    document.cookie = `cartItemCount=${count}; path=/; domain=.grieversandfriends.com;`;
  }

  // Add item (creates unique ID for variations)
  const addItem = (product, variation, quantity = 1) => {
    const itemId = product.id;

    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === itemId);

      if (existingItem) {
        // If item exists, increase quantity
        return prevCart.map((item) =>
          item.id === itemId ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        // Add new item
        return [...prevCart, { ...product, id: itemId, variation, quantity,
          addedAt: Date.now()
         }];
      }
    });

  };

  const updateItem = (itemID, newValue, variation) => {
    setCart((prevCart) => {
        return prevCart.map((item) => 
            item.id === itemID 
                ? { ...item, ...newValue, id: itemID, variation } // Preserve existing properties
                : item
        );
    });
  };

  // Remove item from cart
  const removeItem = (id) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
  }

  // Update item quantity
const updateQuantity = (id, quantity) => {
    setCart((prevCart) =>
        quantity > 0
            ? prevCart.map((item) => (item.id === id ? { ...item, quantity } : item))
            : prevCart.filter((item) => item.id !== id)
    );
};

const incrementItem = (id) => {
    setCart((prevCart) =>
        prevCart.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
    );
};

const decrementItem = (id) => {
    setCart((prevCart) =>
        prevCart.map((item) =>
            item.id === id
                ? item.quantity > 1
                    ? { ...item, quantity: item.quantity - 1 }
                    : null
                : item
        ).filter(Boolean)
    );
};

const applyDiscount = (discount) => {
    setDiscount(discount);
}

const removeDiscount = () => {
    setDiscount({});
}

const discountAmount = () => {
    let cost = cart.reduce((total, item) => total + item.price * item.quantity, 0);

    return Math.abs(((totalPrice() - cost) / 100)).toFixed(2);
}

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

const totalPrice = () => {
    let cost = cart.reduce((total, item) => total + item.price * item.quantity, 0);

    if (discount.amountOff) {
      cost = Math.max(0, cost - discount.amountOff);
    }

    if (discount.percentOff) {
      cost -= (cost * discount.percentOff) / 100;
}

    return cost;
  }

  const formattedTotalPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((totalPrice() / 100).toFixed(2));

  return (
    <CartContext.Provider value={{ cart, discount, cartCount, addItem, updateItem, removeItem, updateQuantity, incrementItem, decrementItem, clearCart, totalPrice, formattedTotalPrice , applyDiscount, removeDiscount, discountAmount }}>
      {children}
    </CartContext.Provider>
  );
}

// Custom hook to use the cart
export function useCart() {
  return useContext(CartContext);
}